import React, { FC, useCallback, useState } from 'react';
import { Button, Form, Input, message, Modal, Popconfirm } from 'antd';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import moment from 'moment';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getContactName } from '../../utils/DataFormatterUtils';
import {
	invoicesRestCrudThunksForBuyer,
	invoicesRestCrudThunksForSupplier,
	markInvoicePendingForBuyer,
	markInvoicePendingForSupplier,
} from '../../thunks/invoices_thunks';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
} from '../../thunks/work_orders_thunks';

const MoveBackToPendingButton: FC<any> = ({
	currentUser,
	markInvoicePending,
	getInvoice,
	appendWorkOrderNotes,
	invoice,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [showModal, setShowModal] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);

	const customButton = (
		<Popconfirm
			onConfirm={() => setShowModal(true)}
			placement="topLeft"
			title="Are you sure you want to move this invoice to pending?"
			okText="Yes"
			cancelText="No"
		>
			<Button type="default" key="cancel" style={{ marginLeft: 12 }} ghost={true}>
				Move back to Pending
			</Button>
		</Popconfirm>
	);

	const handleSubmit = useCallback(
		(values) => {
			const note = {
				text: `${getContactName(currentUser)} moved the invoice back to pending with reason : ${
					values.note
				}`,
				noteAddedBy: nullSafeGet('email', currentUser),
				noteAddedAt: moment.utc(),
			};

			setButtonLoading(true);

			message.open({
				key: 'invoiceDetailMessageUpdating',
				type: 'loading',
				content: 'Marking invoice as pending...',
				duration: 60,
			});
			markInvoicePending(invoice.id).then(() => {
				message.destroy('invoiceDetailMessageUpdating');
				message.success(`Invoice is pending.`);
				setShowModal(false);
				setButtonLoading(false);
				getInvoice(invoice.id);
				appendWorkOrderNotes({
					id: nullSafeGet('workOrder.id', invoice),
					note,
				});
			});
		},
		[markInvoicePending, currentUser, invoice, getInvoice, appendWorkOrderNotes]
	);

	return (
		<>
			<Modal
				title={`Move Invoice back to pending`}
				visible={showModal}
				width={600}
				okText="Confirm"
				onCancel={() => setShowModal(false)}
				onOk={form.submit}
				confirmLoading={buttonLoading}
				closable={false}
			>
				<Form
					form={form}
					onFinish={(values) => {
						setButtonLoading(true);
						handleSubmit(values);
					}}
				>
					<Form.Item
						name="note"
						label="Reason"
						rules={[
							{
								required: true,
								message: `Please add a note on why this invoice is moved back to pending.`,
							},
						]}
						required={true}
					>
						<Input.TextArea />
					</Form.Item>
				</Form>
			</Modal>
			{customButton}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	fetching: state.work_orders.fetching,
	match: ownProps.match,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	markInvoicePending: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markInvoicePendingForSupplier({ id })
				: markInvoicePendingForBuyer({ id })
		),
	getInvoice: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? invoicesRestCrudThunksForSupplier.readOne(id)
				: invoicesRestCrudThunksForBuyer.readOne(id)
		),
	appendWorkOrderNotes: (workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWorkOrderNotesForSupplier(workOrder)
				: appendWorkOrderNotesForBuyer(workOrder)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(MoveBackToPendingButton)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
